import { Inject, Injectable } from "@angular/core";
import { PaymentStatus } from "@bca-confirmation/models/_enums";
import { IStartPaymentResponse } from "@bca-confirmation/models/start-payment-response";
import { IStatusPaymentResponse } from "@bca-confirmation/models/status-payment-response";
import * as confirmationActions from "@bca-confirmation/store/confirmation.actions";
import { ConfirmationActionTypes } from "@bca-confirmation/store/confirmation.actions";
import { ApiService } from "@bca-core/services/api.service";
import { RouterService } from "@bca-core/services/router.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { MessageType, Route } from "@bca-shared/models/_enums";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { EMPTY } from "rxjs";
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class ConfirmationEffects {
  redirectToIssuer = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.SetTransaction),
      filter(
        ({ payload }: confirmationActions.SetTransaction) => payload != null,
      ),
      filter(({ payload: transaction }: confirmationActions.SetTransaction) => {
        return transaction.paymentStatus === PaymentStatus.Start;
      }),
      tap((action: confirmationActions.SetTransaction) => {
        const { customerBankEntryPoint } = action.payload;
        this._routerService.redirectToUrl(customerBankEntryPoint);
        this._store$.dispatch(
          this._createAction(
            new confirmationActions.RedirectToIssuerLoading({ loading: true }),
          ),
        );
      }),
      map(() =>
        this._createAction(new appActions.SetRoute({ route: Route.ISSUER })),
      ),
    ),
  );
  
  startPayment = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.StartPayment),
      withLatestFrom(this._store$.pipe(select(({ receipt }) => receipt))),
      switchMap(([, receipt]) => {
        this._store$.dispatch(
          this._createAction(
            new confirmationActions.StartPaymentLoading({ loading: true }),
          ),
        );
        return this._apiService.startPayment(receipt).pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.START_PAYMENT",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message)),
            );
            return EMPTY;
          }),
        );
      }),
      filter((response: IStartPaymentResponse) => response.success),
      mergeMap(
        ({
          id,
          customerBankEntryPoint,
          referenceNumber,
        }: IStartPaymentResponse) => {
          return this._createActions([
            new confirmationActions.SetTransaction({
              id,
              customerBankEntryPoint,
              paymentStatus: PaymentStatus.Start,
              referenceNumber,
            }),
            new confirmationActions.StartPaymentLoading({ loading: false }),
          ]);
        },
      ),
    ),
  );

  statusPayment = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.StatusPayment),
      withLatestFrom(
        this._store$.pipe(
          select(({ confirmation }) => confirmation.transaction),
        ),
      ),
      switchMap(([, { id }]) => {
        this._store$.dispatch(
          this._createAction(
            new confirmationActions.StatusPaymentLoading({ loading: true }),
          ),
        );
        return this._apiService.statusPayment(id).pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.STATUS_PAYMENT",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message)),
            );
            return EMPTY;
          }),
        );
      }),
      filter((response: IStatusPaymentResponse) => response.success),
      mergeMap((response: IStatusPaymentResponse) => {
        const { paymentStatus, referenceNumber } = response;
        return this._createActions([
          new confirmationActions.SetReferenceNumber({
            referenceNumber: referenceNumber,
          }),
          new confirmationActions.SetPaymentStatus({ status: paymentStatus }),
          new confirmationActions.StatusPaymentLoading({ loading: false }),
          new receiptActions.SetCompletedStatus({ completed: true }),
        ]);
      }),
    ),
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, ConfirmationEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, ConfirmationEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _routerService: RouterService,
    private _apiService: ApiService,
    @Inject(APP_CONFIG) private _config: IAppConfiguration,
  ) {}
}
